import React, { PropsWithChildren } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import ReactModal, { BaseModalProps } from 'react-overlays/Modal';
import styled, { css, DefaultTheme } from 'styled-components';
import { IBlurProps } from '../../fundamentals/blurs/blurs';
import { Heading4 } from '../../components/Headings/Headings';
import { PxToRem, SIZES, PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { IGapSizeProps, IGutterProps } from '../../fundamentals/spaces/space';
import { IBorderRadiusProps, usePanelBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { P } from '../../components/Texts/Texts';
import { Icon, IconSize } from '../../components/Icon/Icon';
import { CloseButton } from '../../components/CloseButton/CloseButton';
import { Overlay } from '../../components/Overlay/Overlay';
import { CtaButtonBar, ICtaButtonBarProps } from '../../components/CtaButtonBar/CtaButtonBar';
import { IAllowBodyStyleOverrideProps } from '../../common/interfaces';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv, BaseImg } from '../../common/BaseElements';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';
import { EORA_EFFECTS } from '../../fundamentals/effects/eoraEffects';
import { ResponsiveGapContainer, ResponsiveSpaceContainer } from '../../fundamentals/spaces/responsive/SpaceContainerResponsive';
import { IMediaSizeProps, MediaSize, MEDIA_SIZES, useMediaSizeCss } from '../../fundamentals/sizes/mediaSize';
import { BackButton, NextButton } from '../../components/BackNextButton/BackNextButton';
import { HR } from '../../components/Divider/Divider';
import { useFilterColourCss } from '../../fundamentals/colours/filter';
import { Alert } from '../../components/Alert/Alert';
import { Tabs } from '../../components/Tabs/Tabs';
import { StatusColour, VARIANTS } from '../../fundamentals/colours/variants';
import { TabList } from '../../components/Tabs/TabList';
import { Tab } from '../../components/Tabs/Tab';
import { ScrollingContainer } from '../../fundamentals/spaces/ScrollingContainer';
import { MaterialIcon } from '../../components/Icon/icons';
import { useModalWrapperCenteredCss, useModalWrapperCss } from '../Modal/Modal';
import { IconButtonSize } from '../../components/IconButton/IconButton';

export const interceptorColours = ({ opaque, text, primary, eoraCoreColours, eoraBrandColours }: IColoursCollection) => ({
  interceptorColours: {
    boxShadow: `${EORA_EFFECTS.EFFECTS.STYLE.SHADOW.SHADOW_DEFAULT}`,
    body: {
      background: eoraCoreColours.BACKGROUND_COLOR.PANEL_COLOR_BACKGROUND
    },
    header: {
      background: eoraBrandColours.BRAND_WL_COLORS.WL_COLOR_PRIMARY,
      text: primary.text,
      light: {
        background: eoraCoreColours.BACKGROUND_COLOR.PANEL_COLOR_BACKGROUND,
        text: text.default
      }
    },
    button: {
      background: opaque.black50NoSwitch.colour
    }
  }
});

export type InterceptorSize = typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE;

export interface IInterceptorProps extends Omit<BaseModalProps, 'children'>, IBlurProps, Pick<ICtaButtonBarProps, 'ctaButtons'>, IAllowBodyStyleOverrideProps {
  size?: InterceptorSize | number;
  title?: string;
  subTitle?: string;
  onClose?: () => void; // Note: controls the close button action
  closable?: boolean;
  startIcon?: MaterialIcon;
  startIconVariant?: StatusColour;
  image?: string;
  imageAlt?: string;
  mediaSize?: MediaSize;
  allowBodyStyleOverride?: boolean;
  showHeaderSeparator?: boolean;
  showFooterSeparator?: boolean;
  showBackNextButtons?: boolean;
  nextButtonShow?: boolean;
  backButtonShow?: boolean;
  onBackButtonClick?: () => void;
  onNextButtonClick?: () => void;
  alert?: { title: string; message: string };
  tabs?: String[];
  onTabClick?: (index: number) => void;
  scrollbar?: boolean | number;
  closeButtonSize?: IconButtonSize;
  /**
   * @description Default colour is primary, this overrides to set to panel body colour
   */
  lightHeader?: boolean;
  /**
   * @description Used to get the popup content only, no overlay or positioning applied
   */
  disableModalBehaviour?: boolean;
}

export const InterceptorSizes: Record<InterceptorSize, PxToRem> = {
  [SIZES.SMALL]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_SMALL],
  [SIZES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_MEDIUM],
  [SIZES.LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_LARGE]
};

const ModalWrapper = styled(ReactModal)`
  ${useModalWrapperCss};
  ${useModalWrapperCenteredCss};
`;

const InterceptorContainer = styled(BaseDiv)<IGutterProps & IGapSizeProps & IBorderRadiusProps & Pick<IInterceptorProps, 'size'>>`
  width: ${({ size }) => (typeof size === 'number' ? `${size}px` : InterceptorSizes[size!])};
  position: relative;
  max-width: 100%;
  background: ${({ theme }) => theme.interceptorColours.body.background};
  box-shadow: ${({ theme }) => theme.interceptorColours.boxShadow};
  ${usePanelBorderRadiusCss()};
`;

const getPanelHeaderColours = (theme: DefaultTheme, lightHeader?: boolean) =>
  lightHeader ? theme.interceptorColours.header.light : theme.interceptorColours.header;

const InterceptorHeader = styled(ResponsiveGapContainer)<IGutterProps & IGapSizeProps & Pick<IInterceptorProps, 'lightHeader'>>`
  background: ${({ theme, lightHeader }) => getPanelHeaderColours(theme, lightHeader).background};
  color: ${({ theme, lightHeader }) => getPanelHeaderColours(theme, lightHeader).text};
  ${usePanelBorderRadiusCss(true)};
`;

// only allow status colours when light header
const StartIcon = styled(Icon)<Pick<IInterceptorProps, 'lightHeader'>>`
  ${({ theme, lightHeader, variant }) =>
    !(variant && lightHeader) &&
    css`
      color: ${useFilterColourCss(getPanelHeaderColours(theme, lightHeader).text)};
    `};
`;

const HeaderCloseButton = styled(CloseButton)<{ absolute?: boolean }>`
  ${({ absolute, theme }) =>
    absolute
      ? css`
          position: absolute;
          right: ${theme.eoraSpaces.xlarge};
          margin-top: ${theme.eoraSpaces.xlarge};
          background: ${theme.interceptorColours.button.background};
        `
      : css`
          margin-left: auto;
        `}
`;

export const InterceptorIconSizes: Record<InterceptorSize, IconSize> = {
  [SIZES.SMALL]: SIZES.SMALL,
  [SIZES.MEDIUM]: SIZES.MEDIUM,
  [SIZES.LARGE]: SIZES.LARGE
};

const InterceptorBody = styled(ResponsiveSpaceContainer)`
  align-items: flex-start;
`;
const BodyContent = styled(ResponsiveGapContainer)`
  align-items: flex-start;
`;

const ScrollContainer = styled(ScrollingContainer)<{ scrollbar?: boolean | number }>`
  width: 100%;
  ${({ scrollbar }) =>
    scrollbar &&
    css`
      max-height: ${scrollbar === true ? '30vh' : `${scrollbar}px`};
    `}
`;

const InterceptorFooter = styled(ResponsiveGapContainer)`
  align-items: flex-start;
`;

const HeaderImage = styled(BaseImg)<IMediaSizeProps>`
  ${useMediaSizeCss};
  width: 100%;
  object-fit: cover;
  ${usePanelBorderRadiusCss(true)};
`;

const BodyHeader = styled(ResponsiveGapContainer)`
  align-items: flex-start;
  width: 100%;
`;

const FooterSeparator = styled(HR)`
  margin-bottom: ${({ theme }) => theme.eoraSpaces.xlarge};
`;

const InterceptorTab = styled(Tabs)`
  width: 100%;
`;

const InterceptorButtons = styled(ResponsiveGapContainer)<{ hasBackButton?: boolean }>`
  justify-content: ${({ hasBackButton }) => (hasBackButton ? 'space-between' : 'flex-end')};
  width: 100%;
`;

interface IIntereptorModalWrapperProps extends Pick<IInterceptorProps, 'disableModalBehaviour' | 'blur'> {
  children: React.ReactElement;
}

const IntereptorModalWrapper: React.FC<IIntereptorModalWrapperProps> = ({ children, disableModalBehaviour, blur, ...rest }) =>
  disableModalBehaviour ? (
    <>{children}</>
  ) : (
    <ModalWrapper renderBackdrop={(props: any) => <Overlay {...props} blur={blur} />} {...rest}>
      {children}
    </ModalWrapper>
  );

export const Interceptor: React.FC<PropsWithChildren<IInterceptorProps>> = ({
  size = SIZES.MEDIUM,
  title,
  subTitle,
  onClose,
  closable = true,
  startIcon,
  startIconVariant,
  image,
  imageAlt,
  mediaSize = MEDIA_SIZES.UWIDE,
  blur = false,
  allowBodyStyleOverride,
  showHeaderSeparator,
  showFooterSeparator,
  showBackNextButtons,
  nextButtonShow,
  backButtonShow,
  onBackButtonClick,
  onNextButtonClick,
  alert,
  tabs,
  onTabClick,
  scrollbar = false,
  ctaButtons,
  closeButtonSize,
  children,
  lightHeader = false,
  disableModalBehaviour = false,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'Interceptor');
  const handleOnClose = () => (onClose ?? rest.onHide)?.();

  return (
    <IntereptorModalWrapper disableModalBehaviour={disableModalBehaviour} blur={blur} {...generateTestId()} {...rest}>
      <InterceptorContainer size={size} {...generateTestId('Container')}>
        {image && <HeaderImage alt={imageAlt} src={image} mediaSize={mediaSize} {...generateTestId(`Image`)} />}
        {image && closable && <HeaderCloseButton variant={'brandAware'} onClick={handleOnClose} size={closeButtonSize ?? SIZES.MEDIUM} absolute />}
        {(title || (!image && closable)) && (
          <InterceptorHeader
            lightHeader={lightHeader}
            gutter={'medium'}
            gutterBottom={lightHeader ? false : 'medium'}
            gap={'medium'}
            column
            {...generateTestId('Header')}
          >
            <ResponsiveGapContainer gap={'small'}>
              {!!startIcon && <StartIcon variant={startIconVariant} lightHeader={lightHeader} name={startIcon} type={'outline'} size={'medium'} />}
              <Heading4 headingType={'SEMI_BOLD'} lineHeight={40} gutterBottom={false}>
                {title}
              </Heading4>
              {closable && (
                <HeaderCloseButton variant={lightHeader ? 'white50' : 'brandAware'} onClick={handleOnClose} size={closeButtonSize ?? SIZES.MEDIUM} />
              )}
            </ResponsiveGapContainer>
            {subTitle && (
              <P gutterBottom={false} textEoraSize='LARGE' textStyle='REGULAR' {...generateTestId(`SubTitle`)}>
                {subTitle}
              </P>
            )}
          </InterceptorHeader>
        )}
        {showHeaderSeparator && <HR {...generateTestId(`HeaderSeparator`)} />}

        <InterceptorBody gutterTop={'medium'}>
          {(showBackNextButtons || tabs || alert) && (
            <BodyHeader gap={'medium'} gutter={'medium'} gutterTop={false} column {...generateTestId(`BodyHeader`)}>
              {showBackNextButtons && (
                <InterceptorButtons hasBackButton={backButtonShow}>
                  {backButtonShow && <BackButton size={SIZES.MEDIUM} showText={false} onClick={onBackButtonClick} />}
                  {nextButtonShow && <NextButton size={SIZES.MEDIUM} showText={false} onClick={onNextButtonClick} />}
                </InterceptorButtons>
              )}
              {tabs && (
                <InterceptorTab size={SIZES.MEDIUM} variant={VARIANTS.PRIMARY} underline='bordered'>
                  <TabList gutterBottom={false}>
                    {tabs.map((item, index) => (
                      <Tab key={index} onClick={() => onTabClick?.(index)} {...generateTestId(`Tab.${item}`)}>
                        {item}
                      </Tab>
                    ))}
                  </TabList>
                </InterceptorTab>
              )}
              {alert && (
                <Alert title={alert.title} variant='error'>
                  {alert.message}
                </Alert>
              )}
            </BodyHeader>
          )}
          <BodyContent gutter={'medium'} gutterTop={false} column>
            <ScrollContainer scrollbar={scrollbar} {...generateTestId(`Body`)}>
              {children}
            </ScrollContainer>
          </BodyContent>
        </InterceptorBody>

        {showFooterSeparator && <FooterSeparator {...generateTestId(`FooterSeparator`)} />}
        {ctaButtons && (
          <InterceptorFooter gutter={'medium'} gutterTop={false} {...generateTestId(`Footer`)}>
            <CtaButtonBar ctaButtons={ctaButtons} {...generateTestId(`CTA`)} />
          </InterceptorFooter>
        )}
      </InterceptorContainer>
    </IntereptorModalWrapper>
  );
};
